import classNames from "classnames";
import { FC, useEffect, useMemo, useState } from "react";
import { MdBolt } from "react-icons/md";

import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { InfoTooltip } from "../../../../components/newUI/info-tooltip";
import CustomSelect from "../../../../components/select/Select";
import { WarningExclaimSvg } from "../../../../components/svg/WarningExclaimSvg";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  AUTOMATION_ACTION_KEY,
  AUTOMATION_CONDITION_KEY,
  AutomationActionKey,
  AutomationActionModel,
  AutomationConditionKey,
  AutomationConditionModel,
  AutomationJobTaskConditionModel,
  useGetAutomationConditionsQuery,
  useGetAutomationJobTaskConditionsQuery,
} from "../../../../slices/AutomationSlice";
import {
  useGetContactQuery,
  useGetOrgContactQuery,
} from "../../../../slices/ContactsSlice";
import { SelectOptionType } from "../../../../types/types";
import {
  AutomationControl,
  AutomationDraft,
  AutomationOptionsKey,
} from "./automation.constants";
import {
  createEmptyDraft,
  findSelectedOption,
  formatDate,
  getSingleSelectValue,
} from "./automation.helpers";
import { AutomationTarget } from "./automation.types";
import { useAutomationLookups } from "./useAutomationLookups";

type Props = {
  isOpen: boolean;
  target: AutomationTarget | null;
  createdBy: string;
  lookups: ReturnType<typeof useAutomationLookups>;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (
    draft: AutomationDraft,
    target: AutomationTarget,
  ) => void | Promise<void>;
};

type SelectAutomationControl = Extract<AutomationControl, { type: "select" }>;
type AutomationConditionViewModel = AutomationConditionModel;

type ContactEmail = {
  emailAddress?: string | null;
};

const AUTOMATION_INFO_TOOLTIP_ID = "add-automation-info-tooltip";
const READY_STATUS_LABEL = "ready";
const FUTURE_EVENTS_WARNING_DESCRIPTION =
  "This automation will only run when the selected status changes in the future.<br />Existing items will not trigger this automation.";

const CONDITION_CONTROLS: Partial<
  Record<AutomationConditionKey, AutomationControl>
> = {
  [AUTOMATION_CONDITION_KEY.WORK_STATUS]: {
    type: "select",
    placeholder: "Select work status",
    optionsKey: "workStatuses",
  },
  [AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE]: {
    type: "dateOffset",
    placeholder: "Days",
    optionsKey: "dateRules",
  },
  [AUTOMATION_CONDITION_KEY.REACHED_DUE_DATE]: {
    type: "none",
  },
  [AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS]: {
    type: "select",
    placeholder: "Select subtask status",
    optionsKey: "subtaskStatuses",
  },
  [AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS]: {
    type: "select",
    placeholder: "Select subtask status",
    optionsKey: "subtaskStatuses",
  },
  [AUTOMATION_CONDITION_KEY.ALL_SUBTASKS_COMPLETED]: {
    type: "none",
  },
  [AUTOMATION_CONDITION_KEY.SUBTASK_REACHED_DUE_DATE]: {
    type: "none",
  },
};

const ACTION_CONTROLS: Partial<Record<AutomationActionKey, AutomationControl>> =
  {
    [AUTOMATION_ACTION_KEY.ASSIGNEE]: {
      type: "select",
      placeholder: "Select team member",
      optionsKey: "users",
    },
    [AUTOMATION_ACTION_KEY.STATUS]: {
      type: "select",
      placeholder: "Select work status",
      optionsKey: "workStatuses",
    },
    [AUTOMATION_ACTION_KEY.SEND_EMAIL]: {
      type: "select",
      placeholder: "Select email template",
      optionsKey: "emailTemplates",
    },
    [AUTOMATION_ACTION_KEY.SEND_NOTIFICATION]: {
      type: "select",
      placeholder: "Select team member",
      optionsKey: "teamMembers",
    },
    [AUTOMATION_ACTION_KEY.CHANGE_STATUS]: {
      type: "select",
      placeholder: "Select subtask status",
      optionsKey: "subtaskStatuses",
    },
  };

const getConditionControl = (condition?: AutomationConditionViewModel) => {
  if (!condition) {
    return undefined;
  }

  return CONDITION_CONTROLS[condition.conditionKey] ?? { type: "none" };
};

const getActionControl = (action?: AutomationActionModel) => {
  if (!action) {
    return undefined;
  }

  return ACTION_CONTROLS[action.actionKey] ?? { type: "none" };
};

const normalizeJobTaskConditions = (
  conditions: AutomationJobTaskConditionModel[],
): AutomationConditionViewModel[] => {
  return conditions.map((condition) => ({
    conditionName: condition.conditionName,
    conditionKey: condition.conditionKey,
    conditionAction: condition.jobTaskConditionAction,
    id: condition.id,
    relayId: condition.relayId,
    createdAt: condition.createdAt,
    createdTimeUTC: condition.createdTimeUTC,
    editedTimeUTC: condition.editedTimeUTC,
  }));
};

const getEmailOptions = (
  emails?: ContactEmail[] | null,
): SelectOptionType[] => {
  const emailAddresses = (emails ?? [])
    .map((email) => email.emailAddress?.trim())
    .filter((email): email is string => Boolean(email));

  return Array.from(new Set(emailAddresses)).map((emailAddress) => ({
    label: emailAddress,
    value: emailAddress,
  }));
};

export const AddAutomationModal: FC<Props> = ({
  isOpen,
  target,
  createdBy,
  lookups,
  isSaving,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<AutomationDraft>(() =>
    createEmptyDraft(target?.type || "work"),
  );
  const [isFutureEventsWarningOpen, setIsFutureEventsWarningOpen] =
    useState(false);

  const {
    data: workConditions = [],
    isFetching: isLoadingWorkConditions,
    isError: isWorkConditionsError,
  } = useGetAutomationConditionsQuery(undefined, {
    skip: !isOpen || target?.type !== "work",
  });

  const {
    data: subtaskConditions = [],
    isFetching: isLoadingSubtaskConditions,
    isError: isSubtaskConditionsError,
  } = useGetAutomationJobTaskConditionsQuery(undefined, {
    skip: !isOpen || target?.type !== "subtask",
  });

  const conditions = useMemo<AutomationConditionViewModel[]>(() => {
    return target?.type === "subtask"
      ? normalizeJobTaskConditions(subtaskConditions)
      : workConditions;
  }, [subtaskConditions, target?.type, workConditions]);

  const isLoadingConditions =
    target?.type === "subtask"
      ? isLoadingSubtaskConditions
      : isLoadingWorkConditions;

  const hasConditionsError =
    target?.type === "subtask"
      ? isSubtaskConditionsError
      : isWorkConditionsError;

  useEffect(() => {
    if (isOpen && target) {
      setDraft(createEmptyDraft(target.type));
      setIsFutureEventsWarningOpen(false);
    }
  }, [isOpen, target]);

  const selectedCondition = useMemo(() => {
    return conditions.find((condition) => condition.id === draft.conditionId);
  }, [conditions, draft.conditionId]);

  const selectedConditionControl = useMemo(() => {
    return getConditionControl(selectedCondition);
  }, [selectedCondition]);

  const selectedAction = useMemo(() => {
    return selectedCondition?.conditionAction
      .map((conditionAction) => conditionAction.action)
      .find((action) => action.id === draft.actionId);
  }, [draft.actionId, selectedCondition?.conditionAction]);

  const selectedActionControl = useMemo(() => {
    return getActionControl(selectedAction);
  }, [selectedAction]);

  const orgId = getLinkedFirm()?.orgId || "";
  const businessContactId =
    target?.work?.businessContactId ||
    target?.work?.customer?.businessContactId ||
    "";
  const customerId =
    target?.work?.customerId || target?.work?.customer?.id || "";
  const shouldLoadEmailOptions =
    isOpen &&
    selectedAction?.actionKey === AUTOMATION_ACTION_KEY.SEND_EMAIL &&
    Boolean(orgId);

  const { data: businessContact, isFetching: isLoadingBusinessContact } =
    useGetOrgContactQuery(
      { orgId, id: businessContactId },
      {
        skip: !shouldLoadEmailOptions || !businessContactId,
      },
    );

  const { data: contact, isFetching: isLoadingContact } = useGetContactQuery(
    { orgId, customerId },
    {
      skip:
        !shouldLoadEmailOptions || Boolean(businessContactId) || !customerId,
    },
  );

  const emailOptions = useMemo<SelectOptionType[]>(() => {
    if (businessContactId) {
      return getEmailOptions(
        businessContact?.emails ?? target?.work?.customer?.emails,
      );
    }

    return getEmailOptions(contact?.emails ?? target?.work?.customer?.emails);
  }, [
    businessContact?.emails,
    businessContactId,
    contact?.emails,
    target?.work?.customer?.emails,
  ]);

  const isLoadingEmailOptions = businessContactId
    ? isLoadingBusinessContact
    : isLoadingContact;
  const shouldSelectEmailRecipient = Boolean(target?.work);

  const conditionOptions = useMemo<SelectOptionType[]>(() => {
    return conditions.map((condition) => ({
      label: condition.conditionName,
      value: condition.id,
    }));
  }, [conditions]);

  const actionOptions = useMemo<SelectOptionType[]>(() => {
    return (
      selectedCondition?.conditionAction.map(({ action }) => ({
        label: action.actionName,
        value: action.id,
      })) ?? []
    );
  }, [selectedCondition?.conditionAction]);

  const isConditionReady = useMemo(() => {
    if (!selectedCondition || !selectedConditionControl) {
      return false;
    }

    if (selectedConditionControl.type === "none") {
      return true;
    }

    if (selectedConditionControl.type === "dateOffset") {
      return Boolean(draft.conditionValue && draft.conditionExtraValue);
    }

    return Boolean(draft.conditionValue);
  }, [
    draft.conditionExtraValue,
    draft.conditionValue,
    selectedCondition,
    selectedConditionControl,
  ]);

  const isActionReady = useMemo(() => {
    if (!selectedAction || !selectedActionControl) {
      return false;
    }

    if (selectedActionControl.type === "none") {
      return true;
    }

    if (selectedAction.actionKey === AUTOMATION_ACTION_KEY.SEND_EMAIL) {
      if (!shouldSelectEmailRecipient) {
        return Boolean(draft.actionValue);
      }

      return Boolean(draft.actionValue && draft.actionExtraValue);
    }

    return Boolean(draft.actionValue);
  }, [
    draft.actionExtraValue,
    draft.actionValue,
    selectedAction,
    selectedActionControl,
    shouldSelectEmailRecipient,
  ]);

  const canSave =
    Boolean(target) &&
    Boolean(selectedCondition) &&
    Boolean(selectedAction) &&
    isConditionReady &&
    isActionReady;

  const isReadyStatusConditionSelected = useMemo(() => {
    if (
      selectedConditionControl?.type !== "select" ||
      (selectedConditionControl.optionsKey !== "workStatuses" &&
        selectedConditionControl.optionsKey !== "subtaskStatuses")
    ) {
      return false;
    }

    const selectedStatus = lookups.options[
      selectedConditionControl.optionsKey
    ]?.find((option) => option.value === draft.conditionValue);

    return selectedStatus?.label?.trim().toLowerCase() === READY_STATUS_LABEL;
  }, [draft.conditionValue, lookups.options, selectedConditionControl]);

  const updateDraft = (patch: Partial<AutomationDraft>) => {
    setDraft((previous) => ({
      ...previous,
      ...patch,
    }));
  };

  const getOptionLabel = (
    optionsKey: AutomationOptionsKey,
    value?: string,
    fallback = "...",
  ) => {
    if (!value) {
      return fallback;
    }

    const option = lookups.options[optionsKey]?.find(
      (item) => item.value === value,
    );

    return option?.label ?? value;
  };

  const getOptions = (control?: AutomationControl) => {
    if (!control || control.type === "none") {
      return [];
    }

    return lookups.options[control.optionsKey] ?? [];
  };

  const getSelectOptionsKey = (
    control?: AutomationControl,
  ): AutomationOptionsKey | null => {
    if (!control || control.type === "none") {
      return null;
    }

    return control.optionsKey;
  };

  const renderBoldValue = (value: string) => {
    return <span className={"font-semibold text-[#253747]"}>{value}</span>;
  };

  const renderPreviewConditionText = () => {
    if (!selectedCondition) {
      return <span className={"text-gray-400"}>choose a trigger</span>;
    }

    switch (selectedCondition.conditionKey) {
      case AUTOMATION_CONDITION_KEY.WORK_STATUS:
        return (
          <>
            has status{" "}
            {renderBoldValue(
              getOptionLabel("workStatuses", draft.conditionValue),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE:
        return (
          <>
            reaches {renderBoldValue(draft.conditionValue || "...")}{" "}
            {getOptionLabel("dateRules", draft.conditionExtraValue)}
          </>
        );

      case AUTOMATION_CONDITION_KEY.REACHED_DUE_DATE:
        return <>reaches the due date</>;

      case AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS:
        return (
          <>
            previous subtask has status{" "}
            {renderBoldValue(
              getOptionLabel("subtaskStatuses", draft.conditionValue),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS:
        return (
          <>
            has status{" "}
            {renderBoldValue(
              getOptionLabel("subtaskStatuses", draft.conditionValue),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.ALL_SUBTASKS_COMPLETED:
        return <>all previous subtasks are completed</>;

      case AUTOMATION_CONDITION_KEY.SUBTASK_REACHED_DUE_DATE:
        return <>has reached its due date</>;

      default:
        return <>{selectedCondition.conditionName}</>;
    }
  };

  const renderPreviewActionText = () => {
    if (!selectedAction) {
      return <span className={"text-gray-400"}>choose an action</span>;
    }

    switch (selectedAction.actionKey) {
      case AUTOMATION_ACTION_KEY.ASSIGNEE:
        return (
          <>
            add {renderBoldValue(getOptionLabel("users", draft.actionValue))} as
            a team member to this{" "}
            {target?.type === "subtask" ? "subtask" : "work item"}
          </>
        );

      case AUTOMATION_ACTION_KEY.STATUS:
        return (
          <>
            change status to{" "}
            {renderBoldValue(getOptionLabel("workStatuses", draft.actionValue))}
          </>
        );

      case AUTOMATION_ACTION_KEY.SEND_EMAIL:
        return (
          <>
            send client email{" "}
            {renderBoldValue(
              `"${getOptionLabel("emailTemplates", draft.actionValue)}"`,
            )}
            {shouldSelectEmailRecipient ? (
              <> to {renderBoldValue(draft.actionExtraValue || "...")}</>
            ) : null}
          </>
        );

      case AUTOMATION_ACTION_KEY.SEND_NOTIFICATION:
        return (
          <>
            notify{" "}
            {renderBoldValue(getOptionLabel("teamMembers", draft.actionValue))}
          </>
        );

      case AUTOMATION_ACTION_KEY.CHANGE_STATUS:
        return (
          <>
            change current subtask status to{" "}
            {renderBoldValue(
              getOptionLabel("subtaskStatuses", draft.actionValue),
            )}
          </>
        );

      default:
        return <>{selectedAction.actionName}</>;
    }
  };

  const renderPreviewContent = () => {
    if (!target) {
      return null;
    }

    const entityLabel =
      target.entityLabel ??
      (target.type === "subtask" ? "subtask" : "work item");

    return (
      <>
        <span>When </span>
        <span>{entityLabel}</span>
        {!target.hideTitleInPreview && (
          <>
            <span> </span>
            {renderBoldValue(target.title)}
          </>
        )}
        <span> </span>
        {renderPreviewConditionText()}
        <span>, </span>
        {renderPreviewActionText()}
      </>
    );
  };

  const handleConditionChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);

    updateDraft({
      conditionId: option?.value,
      conditionKey: conditions.find(
        (condition) => condition.id === option?.value,
      )?.conditionKey,
      conditionValue: "",
      conditionExtraValue: "",
      actionId: undefined,
      actionKey: undefined,
      actionValue: "",
      actionExtraValue: "",
    });
  };

  const handleConditionValueChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);

    updateDraft({
      conditionValue: option?.value ?? "",
    });
  };

  const handleConditionExtraValueChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);

    updateDraft({
      conditionExtraValue: option?.value ?? "",
    });
  };

  const handleActionChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);
    const nextActionKey = selectedCondition?.conditionAction.find(
      ({ action }) => action.id === option?.value,
    )?.action.actionKey;

    updateDraft({
      actionId: option?.value,
      actionKey: nextActionKey,
      actionValue: "",
      actionExtraValue: "",
    });
  };

  const handleActionValueChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);

    updateDraft({
      actionValue: option?.value ?? "",
    });
  };

  const handleActionExtraValueChange = (
    value: SelectOptionType | readonly SelectOptionType[],
  ) => {
    const option = getSingleSelectValue(value);

    updateDraft({
      actionExtraValue: option?.value ?? "",
    });
  };

  const handleSave = async () => {
    if (!canSave || !target || isSaving) {
      return;
    }

    if (isReadyStatusConditionSelected) {
      setIsFutureEventsWarningOpen(true);
      return;
    }

    await onSave(draft, target);
  };

  const handleConfirmFutureEventsWarning = async () => {
    if (!canSave || !target || isSaving) {
      return;
    }

    setIsFutureEventsWarningOpen(false);
    await onSave(draft, target);
  };

  const renderSingleSelect = ({
    options,
    value,
    placeholder,
    onChange,
    isLoading,
    disabled,
    withAvatar,
    width = "390px",
  }: {
    options: SelectOptionType[];
    value?: string;
    placeholder?: string;
    onChange: (value: SelectOptionType | readonly SelectOptionType[]) => void;
    isLoading?: boolean;
    disabled?: boolean;
    withAvatar?: boolean;
    width?: string;
  }) => {
    return (
      <div className={classNames(disabled && "pointer-events-none opacity-60")}>
        <CustomSelect
          options={options}
          value={findSelectedOption(options, value)}
          onChange={onChange}
          placeholder={placeholder}
          isLoading={isLoading}
          withMenu
          withAvatar={withAvatar}
          selectStyle={{ fontWeight: 400, fullWidth: true, small: true, width }}
        />
      </div>
    );
  };

  const renderUnavailableHint = (control?: AutomationControl) => {
    const optionsKey = getSelectOptionsKey(control);

    if (!optionsKey || getOptions(control).length > 0) {
      return null;
    }

    return (
      <div className={"mt-2 text-xs text-amber-700"}>
        No options are available for this field yet. The automation can be
        completed when the related data is loaded.
      </div>
    );
  };

  const renderConditionSecondControl = () => {
    if (!selectedConditionControl || selectedConditionControl.type === "none") {
      return null;
    }

    const options = getOptions(selectedConditionControl);

    if (selectedConditionControl.type === "dateOffset") {
      return (
        <>
          <div className={"grid grid-cols-[90px_1fr] gap-2"}>
            <input
              className={
                "h-[32px] w-full rounded-lg border border-[#C8D2DE] bg-white px-3 text-sm font-normal text-[#253747] outline-none focus:border-[#6938EF]"
              }
              type={"number"}
              min={1}
              placeholder={selectedConditionControl.placeholder}
              value={draft.conditionValue}
              onChange={(event) =>
                updateDraft({ conditionValue: event.target.value })
              }
            />

            {renderSingleSelect({
              options,
              value: draft.conditionExtraValue,
              placeholder: "Select date rule",
              onChange: handleConditionExtraValueChange,
              width: "292px",
            })}
          </div>

          {renderUnavailableHint(selectedConditionControl)}
        </>
      );
    }

    return (
      <>
        {renderSingleSelect({
          options,
          value: draft.conditionValue,
          placeholder: selectedConditionControl.placeholder,
          onChange: handleConditionValueChange,
          isLoading: lookups.isLoadingStatuses,
        })}

        {renderUnavailableHint(selectedConditionControl)}
      </>
    );
  };

  const renderActionSecondControl = () => {
    if (!selectedActionControl || selectedActionControl.type === "none") {
      return null;
    }

    if (selectedActionControl.type === "dateOffset") {
      return null;
    }

    const control = selectedActionControl as SelectAutomationControl;
    const options = getOptions(control);
    const optionsKey = control.optionsKey;
    const isUserSelect = optionsKey === "users" || optionsKey === "teamMembers";
    const isSendEmailAction =
      selectedAction?.actionKey === AUTOMATION_ACTION_KEY.SEND_EMAIL;

    if (isSendEmailAction) {
      if (!shouldSelectEmailRecipient) {
        return (
          <>
            {renderSingleSelect({
              options,
              value: draft.actionValue,
              placeholder: control.placeholder,
              onChange: handleActionValueChange,
              isLoading: lookups.isLoading,
            })}

            {renderUnavailableHint(control)}
          </>
        );
      }

      return (
        <>
          {renderSingleSelect({
            options: emailOptions,
            value: draft.actionExtraValue,
            placeholder: "Select recipient email",
            onChange: handleActionExtraValueChange,
            isLoading: isLoadingEmailOptions,
            disabled: isLoadingEmailOptions,
          })}

          {!isLoadingEmailOptions && emailOptions.length === 0 && (
            <div className={"mt-2 text-xs text-amber-700"}>
              No contact email addresses are available. Add an email address to
              the contact before creating this automation.
            </div>
          )}

          <div className={"mt-2"}>
            {renderSingleSelect({
              options,
              value: draft.actionValue,
              placeholder: control.placeholder,
              onChange: handleActionValueChange,
              isLoading: lookups.isLoading,
            })}
          </div>

          {renderUnavailableHint(control)}
        </>
      );
    }

    return (
      <>
        {renderSingleSelect({
          options,
          value: draft.actionValue,
          placeholder: control.placeholder,
          onChange: handleActionValueChange,
          isLoading: isUserSelect ? lookups.isLoadingUsers : lookups.isLoading,
          withAvatar: isUserSelect,
        })}

        {renderUnavailableHint(control)}
      </>
    );
  };

  const renderStatusMessage = () => {
    if (hasConditionsError) {
      return (
        <div
          className={
            "rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
          }>
          Automation options could not be loaded. Please try again.
        </div>
      );
    }

    if (!isLoadingConditions && conditions.length === 0) {
      return (
        <div
          className={
            "rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          }>
          {target?.type === "subtask"
            ? "Subtask automation options are not available yet."
            : "No automation conditions are available yet."}
        </div>
      );
    }

    return null;
  };

  if (!isOpen || !target) {
    return null;
  }

  const statusMessage = renderStatusMessage();

  return (
    <div className={"modal modal-open"}>
      <div className={"modal-box w-[450px] max-w-[calc(100vw-32px)] p-0"}>
        <div
          className={
            "mb-4 flex w-full items-center justify-start border-b border-gray-200 px-4 py-2 text-center text-base font-semibold"
          }>
          <span>
            New {target.type === "work" ? "Work" : "Subtask"} Automation
          </span>
          <InfoTooltip
            id={AUTOMATION_INFO_TOOLTIP_ID}
            ariaLabel={"Automation timing information"}
            place={"top"}
            size={20}
            className={"ml-2 h-5 w-5"}
            tooltipClassName={
              "!max-w-[290px] !rounded-lg !px-4 !py-3 !text-sm !leading-6"
            }
            content={
              <div>
                Automations only apply to future status changes.
                <br />
                If the condition is already met, the automation will not run
                until the status changes again.
              </div>
            }
          />
        </div>

        {statusMessage && <div className={"mb-4 px-6"}>{statusMessage}</div>}

        <div className={"mb-4 px-6"}>
          <div
            className={"mb-2 text-xs font-semibold uppercase text-[#7C66F0]"}>
            IF
          </div>

          {renderSingleSelect({
            options: conditionOptions,
            value: draft.conditionId,
            placeholder: "Select condition",
            onChange: handleConditionChange,
            isLoading: isLoadingConditions,
            disabled: hasConditionsError || conditionOptions.length === 0,
          })}

          <div className={"mt-2"}>{renderConditionSecondControl()}</div>
        </div>

        <div className={"mb-4 px-6"}>
          <div
            className={"mb-2 text-xs font-semibold uppercase text-[#7C66F0]"}>
            THEN
          </div>

          {renderSingleSelect({
            options: actionOptions,
            value: draft.actionId,
            placeholder: selectedCondition
              ? "Select action"
              : "Select condition first",
            onChange: handleActionChange,
            disabled: !selectedCondition || actionOptions.length === 0,
          })}

          <div className={"mt-2"}>{renderActionSecondControl()}</div>
        </div>

        <div className={"mb-4 px-6"}>
          <div
            className={"mb-2 flex items-center gap-2 text-sm text-[#253747]"}>
            <MdBolt className={"h-5 w-5 text-yellow-300"} />
            <span>Automation Summary</span>
          </div>

          <div
            className={
              "rounded-lg border border-gray-200 px-3 py-2 text-sm leading-[22px] text-[#253747]"
            }>
            {renderPreviewContent()}
          </div>

          <div className={"mt-2 text-xs text-gray-500"}>
            Created by {createdBy} on {formatDate(new Date().toISOString())}
          </div>
        </div>

        <div
          className={"mt-4 flex justify-end gap-3 p-3 text-sm font-semibold"}>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747] disabled:opacity-75"
            }>
            Cancel
          </button>

          <button
            type={"button"}
            disabled={!canSave || isSaving}
            onClick={handleSave}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
            }>
            {isSaving ? "Saving" : "Save"}
          </button>
        </div>

        {isFutureEventsWarningOpen && (
          <SettingsConfirmModal
            onClose={() => setIsFutureEventsWarningOpen(false)}
            onConfirm={handleConfirmFutureEventsWarning}
            isLoading={isSaving}
            icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
            title={"Future Events Only"}
            description={FUTURE_EVENTS_WARNING_DESCRIPTION}
            confirmText={"Save Anyway"}
            variant={"primary"}
            iconVariant={"warning"}
          />
        )}
      </div>
    </div>
  );
};
